/**
 * Service ladder — "your options to work with us".
 *
 * Ported from the Claude Design frame `Service Ladder v4.dc.html`. The design's
 * runtime harness (x-dc / x-import / image-slot / _ds_bundle.js) is deliberately
 * not carried over — that would mean running a second rendering system beside
 * React. Instead its design-system Buttons map onto the site's existing
 * .btn-cyan / .btn-ghost-cyan classes, and the token values it used are already
 * in index.css as --cyan and friends.
 *
 * Layout is fluid by construction (flex-wrap + clamp), exactly as the frame
 * specified, so it needs no breakpoint branching and survives the homepage's
 * 390px prerender pass unchanged.
 */

import React from "react";
// The frame's CTAs pointed at placeholder anchors (#grow, #plan, #monthly) with
// no matching sections. They split by rung: the free review is funnel entry,
// while the two paid tiers are buying signals and go straight to a strategist
// rather than back through the free offer.
import { STRATEGIST_URL, VIDEO_REVIEW_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

/**
 * Still frame for the example review video. The design left this as an
 * image-slot ("Drop a still from a real review video").
 *
 * Until a real still exists the whole left panel is not rendered at all — a
 * play button over an empty box promises a video that isn't there, which
 * undercuts the exact offer this rung is selling. The copy column then fills
 * the card on its own and still tells people they get a video. Set this to an
 * image path and the panel returns; nothing else needs changing.
 */
const REVIEW_STILL: string | null = null;

/**
 * With the video panel hidden the copy column spans the whole card, so the
 * column keeps its full width (an inset one leaves a visible empty band where
 * the video used to sit) and the text inside is capped to a readable measure
 * instead. Once REVIEW_STILL is set the panel takes half the card back and no
 * cap is needed.
 */
const measure: React.CSSProperties = REVIEW_STILL ? {} : { maxWidth: "620px" };

const SPRINT_WEEKS = [
  { week: "WEEK 1", text: "We find the opportunities" },
  { week: "WEEK 2", text: "We build the plan with your team" },
  { week: "WEEK 3–4", text: "We ship the quick wins together" },
];

const REVIEW_POINTS = [
  "The buyer questions sending customers to competitors — and what it's costing you",
  "Why AI names them, not you, when buyers are ready to spend",
  "The exact moves to win that revenue back",
];

const FULL_STACK_DISCIPLINES = [
  "Strategy", "Technical SEO", "AEO", "Content production",
  "Link building", "Web design", "Web development", "Analytics",
];

const cardShell: React.CSSProperties = {
  flex: "1 1 340px", minWidth: 0,
  background: "rgba(255,255,255,0.045)",
  border: "1px solid rgba(255,255,255,0.10)",
  backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
  borderRadius: "18px",
  padding: "clamp(24px,2.6vw,32px)",
  display: "flex", flexDirection: "column", gap: "16px",
};

const kicker: React.CSSProperties = {
  fontSize: "12px", fontWeight: 700, letterSpacing: "0.14em",
  color: "rgba(255,255,255,0.5)",
};

const priceTag: React.CSSProperties = {
  fontSize: "14px", fontWeight: 700, color: "rgba(255,255,255,0.82)",
};

const cardRule: React.CSSProperties = {
  borderTop: "1px solid rgba(255,255,255,0.08)",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

export default function ServiceLadder() {
  return (
    <section
      className="sl"
      aria-labelledby="service-ladder-heading"
      style={{ background: "var(--ink-900)", fontFamily: ff, color: "#fff", position: "relative", overflow: "hidden" }}
    >
      {/* Cyan wash behind the top of the section */}
      <div aria-hidden="true" style={{
        position: "absolute", top: "-240px", left: "50%", transform: "translateX(-50%)",
        width: "1000px", height: "600px", borderRadius: "50%",
        background: "rgba(87,215,226,0.13)", filter: "blur(175px)", pointerEvents: "none",
      }} />
      {/* Grid, faded out toward the bottom */}
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          "linear-gradient(rgba(87,215,226,0.05) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(87,215,226,0.05) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
        maskImage: "linear-gradient(#000, transparent 70%)",
        WebkitMaskImage: "linear-gradient(#000, transparent 70%)",
      }} />

      <div style={{ position: "relative", maxWidth: "1080px", margin: "0 auto", padding: "96px 20px 100px" }}>

        {/* ── Section heading ── */}
        <div style={{ maxWidth: "640px", marginBottom: "52px" }}>
          <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.2em", color: "var(--cyan)", marginBottom: "18px" }}>
            YOUR OPTIONS TO WORK WITH US
          </div>
          <h2 id="service-ladder-heading" className="sl-pretty" style={{
            fontSize: "clamp(32px,5vw,50px)", lineHeight: 1.06, fontWeight: 800,
            letterSpacing: "-0.033em", margin: "0 0 16px",
          }}>
            Flexible offers, <span style={{ color: "var(--cyan)" }}>from lean teams to large enterprises.</span>
          </h2>
          <p style={{ fontSize: "18px", lineHeight: 1.6, color: "rgba(255,255,255,0.72)", margin: 0 }}>
            Start by submitting your site for a risk-free video analysis of the revenue you're leaving on the
            table — from both a website and an AEO perspective. From there, run a one-time project with us, or
            hand it to our full team as a managed service.
          </p>
        </div>

        {/* ── Rung 1: the free video review ── */}
        <div style={{
          border: "1px solid rgba(87,215,226,0.32)", borderRadius: "18px", overflow: "hidden",
          boxShadow: "0 20px 60px rgba(87,215,226,0.12)",
          background: "var(--cyan-fill)",
          backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "stretch" }}>
            {/* Video still — only once a real still exists, see REVIEW_STILL. */}
            {REVIEW_STILL && (
              <div style={{
                flex: "1 1 460px", minWidth: "300px", position: "relative",
                background: "#0c0c0c", minHeight: "clamp(280px,26vw,420px)", display: "flex",
              }}>
                <img src={REVIEW_STILL} alt="Still from an example personalized video review"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                <div aria-hidden="true" style={{
                  position: "absolute", inset: 0, pointerEvents: "none",
                  background: "linear-gradient(180deg, rgba(8,10,10,0.05) 40%, rgba(8,10,10,0.72) 100%)",
                }} />
                <div aria-hidden="true" style={{ position: "absolute", right: "18px", top: "18px", pointerEvents: "none" }}>
                  <div style={{
                    width: "72px", height: "72px", borderRadius: "50%",
                    background: "linear-gradient(135deg, #85e2e9, #20b0bc)",
                    boxShadow: "0 6px 40px rgba(87,215,226,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="22" height="25" viewBox="0 0 18 20" fill="#121212"><path d="M17 10L0 20V0z" /></svg>
                  </div>
                </div>
                <div aria-hidden="true" style={{ position: "absolute", left: "18px", bottom: "18px", pointerEvents: "none" }}>
                  <div style={{
                    fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", color: "#fff",
                    background: "rgba(10,10,10,0.62)", border: "1px solid rgba(255,255,255,0.16)",
                    borderRadius: "88px", padding: "5px 11px",
                  }}>
                    EXAMPLE REVIEW · 12:04
                  </div>
                </div>
              </div>
            )}

            {/* Offer copy */}
            <div style={{
              flex: "1 1 460px", minWidth: "300px", padding: "clamp(24px,3vw,40px)",
              display: "flex", flexDirection: "column", gap: "14px", justifyContent: "center",
              background: "rgba(10,12,12,0.35)",
            }}>
              <div>
                <div style={{ fontSize: "15px", fontWeight: 700, letterSpacing: "-0.01em", color: "#fff", marginBottom: "8px" }}>
                  Personalized Video Review — Site + AI Visibility
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap" }}>
                  <div style={{
                    fontSize: "11px", fontWeight: 800, letterSpacing: "0.14em", color: "#121212",
                    background: "linear-gradient(135deg, #85e2e9, #20b0bc)", borderRadius: "88px", padding: "4px 11px",
                  }}>
                    YOURS FREE
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "rgba(255,255,255,0.62)" }}>
                    Recorded personally for you
                  </div>
                </div>
              </div>

              <h3 className="sl-pretty" style={{
                fontSize: "clamp(26px,3vw,38px)", fontWeight: 800, letterSpacing: "-0.034em",
                lineHeight: 1.08, margin: 0, ...measure,
              }}>
                Your competitors are winning revenue that should be yours.{" "}
                <span style={{ color: "var(--cyan)" }}>See exactly how — free.</span>
              </h3>

              <p style={{ fontSize: "17px", lineHeight: 1.6, color: "rgba(255,255,255,0.8)", margin: 0, ...measure }}>
                A top-1% SEO who's driven millions in revenue records a personal video of your site — and shows you:
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", ...measure }}>
                {REVIEW_POINTS.map((point) => (
                  <div key={point} style={{ display: "flex", gap: "12px", alignItems: "baseline" }}>
                    <div aria-hidden="true" style={{ flex: "none", color: "var(--cyan)", fontSize: "15px", lineHeight: 1.55 }}>→</div>
                    <div style={{ fontSize: "15px", lineHeight: 1.55, color: "rgba(255,255,255,0.82)" }}>{point}</div>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: "15px", lineHeight: 1.55, color: "rgba(255,255,255,0.6)", margin: "4px 0 0", ...measure }}>
                In your inbox in days — yours to act on with or without us.
              </p>
            </div>
          </div>

          <div style={{
            display: "flex", gap: "22px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
            padding: "22px clamp(20px,3vw,32px)",
            borderTop: "1px solid rgba(87,215,226,0.24)", background: "rgba(10,12,12,0.5)",
          }}>
            <div style={{ fontSize: "14px", fontWeight: 600, color: "rgba(255,255,255,0.66)" }}>
              Covers ChatGPT, Google AI Overviews, Perplexity and organic rankings.
            </div>
            <a href={VIDEO_REVIEW_URL} className="btn-cyan" style={{ height: "52px", padding: "0 28px", fontSize: "15px" }}>
              Get my free video review →
            </a>
          </div>
        </div>

        {/* ── Rungs 2 and 3 ── */}
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginTop: "14px" }}>

          {/* The AEO Sprint */}
          <div style={cardShell}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
              <div style={kicker}>30-DAY SPRINT</div>
              <div style={priceTag}>$2,000</div>
            </div>
            <div>
              <h3 style={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, margin: "0 0 10px" }}>
                The AEO Sprint
              </h3>
              <p style={{ fontSize: "16px", lineHeight: 1.6, color: "rgba(255,255,255,0.78)", margin: 0 }}>
                A top-1% specialist embedded with your team for 30 days. We find the opportunities, build the plan
                with you, and ship the quick wins before it ends.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px", padding: "18px 0", ...cardRule }}>
              {SPRINT_WEEKS.map((w) => (
                <div key={w.week} style={{ display: "flex", gap: "14px", alignItems: "baseline" }}>
                  {/* 72px, not the frame's 64px: "WEEK 3–4" is ~68px at this
                      size and tracking, so 64 wrapped it onto two lines. */}
                  <div style={{
                    flex: "0 0 72px", whiteSpace: "nowrap",
                    fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", color: "rgba(255,255,255,0.42)",
                  }}>
                    {w.week}
                  </div>
                  <div style={{ fontSize: "15px", lineHeight: 1.5, color: "rgba(255,255,255,0.82)" }}>{w.text}</div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: "15px", lineHeight: 1.55, color: "rgba(255,255,255,0.88)", fontWeight: 600, margin: 0 }}>
              You end the month with work live — not a report.
            </p>

            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "10px", alignItems: "flex-start" }}>
              <a href={STRATEGIST_URL} className="btn-cyan" style={{ height: "44px", padding: "0 22px", fontSize: "14.5px" }}>
                Start the sprint →
              </a>
            </div>
          </div>

          {/* Full-Stack SEO and AEO */}
          <div style={cardShell}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
              <div style={kicker}>MONTHLY — DONE FOR YOU</div>
              <div style={priceTag}>From $5,000/mo</div>
            </div>
            <div>
              <h3 style={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, margin: "0 0 10px" }}>
                Full-Stack SEO and AEO
              </h3>
              <p style={{ fontSize: "16px", lineHeight: 1.6, color: "rgba(255,255,255,0.78)", margin: "0 0 10px" }}>
                The Indexed Full-Stack Search Method™ run for you end-to-end — strategy, technical, content,
                authority, GEO/AEO visibility and reporting. Nothing to manage.
              </p>
              <p style={{ fontSize: "14px", lineHeight: 1.5, color: "rgba(255,255,255,0.5)", margin: 0 }}>
                For businesses that want the outcome without building an internal SEO team.
              </p>
            </div>

            <div style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))",
              gap: "8px 20px", padding: "16px 0", ...cardRule,
            }}>
              {FULL_STACK_DISCIPLINES.map((d) => (
                <div key={d} style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>{d}</div>
              ))}
            </div>

            <div style={{ fontSize: "16px", lineHeight: 1.55, color: "#fff", fontWeight: 600 }}>
              Reported to pipeline and revenue against targets we agree with you —{" "}
              <span style={{ color: "var(--cyan)" }}>not rankings.</span>
            </div>

            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "10px", alignItems: "flex-start" }}>
              <a href={STRATEGIST_URL} className="btn-cyan" style={{ height: "44px", padding: "0 22px", fontSize: "14.5px" }}>
                Grow with Indexed →
              </a>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .sl-pretty { text-wrap: pretty; }
      `}</style>
    </section>
  );
}
